import type { Video } from '@/types/video';
import { durationOptions } from '@/mocks/categories';
import { isWithinTime } from '@/utils/video';

export interface VideoQuery {
  category?: string;
  region?: string;
  duration?: string;
  time?: string;
  keyword?: string;
  sort?: 'latest' | 'hot';
}

export function filterVideos(source: Video[], query: VideoQuery): Video[] {
  const keyword = query.keyword?.trim().toLowerCase() ?? '';

  const result = source.filter((v) => {
    if (query.category && v.category !== query.category) return false;

    if (query.region && query.region !== 'all' && v.region !== query.region) return false;

    if (query.duration && query.duration !== 'all') {
      const opt = durationOptions.find((d) => d.id === query.duration);
      if (opt && !(v.duration >= opt.min && v.duration < opt.max)) return false;
    }

    if (query.time && query.time !== 'all' && !isWithinTime(v.publishDate, query.time)) {
      return false;
    }

    if (keyword) {
      const haystack = `${v.title} ${v.author} ${v.tags.join(' ')}`.toLowerCase();
      if (!haystack.includes(keyword)) return false;
    }

    return true;
  });

  if (query.sort === 'hot') {
    result.sort((a, b) => b.views - a.views);
  } else {
    result.sort(
      (a, b) => new Date(b.publishDate).getTime() - new Date(a.publishDate).getTime(),
    );
  }

  return result;
}

export function paginate<T>(list: T[], page: number, pageSize: number) {
  const totalPages = Math.max(1, Math.ceil(list.length / pageSize));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const start = (safePage - 1) * pageSize;
  return {
    items: list.slice(start, start + pageSize),
    totalPages,
    page: safePage,
  };
}